/* eslint-disable no-unreachable */
import React, { useEffect, useState, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";

import MapSinglePoint from "components/shared/MapSinglePoint";
import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";
import FormFooter from "shared/components/FormElements/FormFooter";

import Input from "shared/components/FormElements/Input";
import CustomSelect from "shared/components/FormElements/Select";
import NotFound from "shared/components/UIElements/NotFound";

import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";
import { isActiveOptions_tr, isActiveOptions_en } from "shared/util/types";
import AddParentButton from "features/relations/AddParentButton";
import ParentRelationRow from "features/relations/ParentRelationRow";
import GenericCard from "components/ui/cards/GenericCard";
import { getCurrentLanguage } from "shared/utility/lang";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";

const StudentItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  //standard code
  const { t } = useTranslation();

  const currentLanguage = getCurrentLanguage();
  const isActiveOptions = currentLanguage === "en" ? isActiveOptions_en : isActiveOptions_tr;

  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // context
  const { InsertRelation, UpdateRelation, DeleteRelation, studentSchoolOptions, relations, students, userMap } =
    useContext(StoreContext);

  // item info
  const itemType = "student";
  const endPointUpdate = "students";
  const endPointDelete = "students";
  const endPointCreate = "students";

  // item special states
  const [markerLocationMorning, setMarkerLocationMorning] = useState();
  const [studentAddressMorning, setStudentAddressMorning] = useState();
  const [addressMorningInSync, setAddressMorningInSync] = useState(false);
  const [addressMorningQuality, setAddressMorningQuality] = useState(null);

  const [markerLocationEvening, setMarkerLocationEvening] = useState();
  const [studentAddressEvening, setStudentAddressEvening] = useState();
  const [addressEveningInSync, setAddressEveningInSync] = useState(false);
  const [addressEveningQuality, setAddressEveningQuality] = useState(null);

  const [studentCurrentParents, setStudentCurrentParents] = useState([]);

  // item special functions
  const mapDataChangeHandlerMorning = (address, location, inSync, quality) => {
    setMarkerLocationMorning(location);
    setStudentAddressMorning(address);
    setAddressMorningInSync(inSync);
    setAddressMorningQuality(quality);
    inputHandler("addressgeton", address, true);
  };

  const mapDataChangeHandlerEvening = (address, location, inSync, quality) => {
    setMarkerLocationEvening(location);
    setStudentAddressEvening(address);
    setAddressEveningInSync(inSync);
    setAddressEveningQuality(quality);
    inputHandler("addressgetoff", address, true);
  };

  // formstates
  const [formState, inputHandler] = useForm(
    {
      school: { value: "", isValid: true },
      identifier: { value: "", isValid: true },
      name: { value: "", isValid: true },
      level: { value: "", isValid: true },
      addressgeton: { value: null, isValid: true },
      addressgetoff: { value: null, isValid: true },
      addressqualitygeton: { value: null, isValid: true },
      addressqualitygetoff: { value: null, isValid: true },
      isActive: { value: "", isValid: true },
      phonenumber: { value: "-", isValid: true },
      email: { value: "-", isValid: true },
    },
    true,
  );

  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const currentStudent = students.find((student) => student.id === itemId);
      setLoadedItem(currentStudent);
      setStudentAddressMorning(currentStudent?.addressgeton);

      const currentParents = relations
        .filter((relation) => relation.student === itemId)
        .map((relation) => {
          const parent = userMap[relation.parent];
          if (!parent) {
            return null; // Skip if parent not found
          }
          return parent;
        })
        .filter((parent) => parent !== null); // Remove null values from array
      setStudentCurrentParents(currentParents);

      if (currentStudent?.locationgeton?.lat && currentStudent?.locationgeton?.lng) {
        setMarkerLocationMorning(currentStudent.locationgeton);
        setAddressMorningInSync(true);
        setAddressMorningQuality(currentStudent?.addressqualitygeton);
      } else {
        setMarkerLocationMorning(null);
        setAddressMorningInSync(false);
        setAddressMorningQuality(null);
      }

      setStudentAddressEvening(currentStudent?.addressgetoff);
      if (currentStudent?.locationgetoff?.lat && currentStudent?.locationgetoff?.lng) {
        setMarkerLocationEvening(currentStudent.locationgetoff);
        setAddressEveningInSync(true);
        setAddressEveningQuality(currentStudent?.addressqualitygetoff);
      } else {
        setMarkerLocationEvening(null);
        setAddressEveningInSync(false);
        setAddressEveningQuality(null);
      }
    } catch (err) {}
    setIsItemLoaded(true);
  }, [itemId]);

  useEffect(() => {
    fetchItem();
    // eslint-disable-next-line
  }, [itemId, fetchItem]);

  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    const endpoint = isNewItem
      ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
      : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

    const postType = isNewItem ? "POST" : "PATCH";

    const infoToBeSent = {
      school: formState.inputs.school.value,
      identifier: formState.inputs.identifier.value || "",
      name: formState.inputs.name.value,
      phonenumber: formState.inputs.phonenumber.value,
      email: formState.inputs.email.value,
      level: formState.inputs.level.value,
      addressgeton: studentAddressMorning,
      locationgeton: markerLocationMorning,
      addressgetoff: studentAddressEvening,
      locationgetoff: markerLocationEvening,
      isActive: formState.inputs.isActive.value,
      addressqualitygeton: addressMorningQuality,
      addressqualitygetoff: addressEveningQuality,
    };

    //standard code
    try {
      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);

          const relationPromises = studentCurrentParents.map(async (parent) => {
            const relationData = {
              student: responseData[itemType].id,
              parent: parent.id,
              isenabled: true,
              approachtogeton: parent.approachtogeton || "",
              approachtogetoff: parent.approachtogetoff || "",
              arrivedschool: parent.arrivedschool || "",
              leavedschool: parent.leavedschool || "",
              distancegeton: parent.distancegeton || "",
              distancegetoff: parent.distancegetoff || "",
              studentnotgeton: parent.studentnotgeton || false,
            };

            const newRelation = await sendRequest(
              `${process.env.REACT_APP_BACKEND_URL}/relations`,
              "POST",
              JSON.stringify(relationData),
            );
            return newRelation;
          });

          const newRelations = await Promise.all(relationPromises);
          newRelations.forEach((relationData) => {
            InsertRelation(relationData.relation);
          });
        } else {
          UpdateItem(responseData[itemType]);

          const relationsData = await sendRequest(
            `${process.env.REACT_APP_BACKEND_URL}/relations/updateallforastudent`,
            "POST",
            JSON.stringify({
              studentId: itemId,
              parents: studentCurrentParents,
            }),
          );

          const currentRelations = relations.filter((relation) => relation.student === itemId);

          relationsData.relations.forEach((relation) => {
            const existingRelation = currentRelations.find((r) => r.id === relation.id);
            if (existingRelation) {
              UpdateRelation(relation);
            } else {
              InsertRelation(relation);
            }
          });

          const deletedRelations = currentRelations.filter(
            (r) => !relationsData.relations.some((newR) => newR.id === r.id),
          );
          deletedRelations.forEach((relation) => {
            DeleteRelation(relation.id);
          });
          // await UpdateRelations();

          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      if (err?.code) {
        window.toast.error(t("httpCodes." + err.code));
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  //standard code
  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemType}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => {
          setGeneratedItemId(null);
          // Reset map states for morning
          setMarkerLocationMorning(null);
          setStudentAddressMorning("");
          setAddressMorningInSync(false);
          setAddressMorningQuality(null);
          // Reset map states for evening
          setMarkerLocationEvening(null);
          setStudentAddressEvening("");
          setAddressEveningInSync(false);
          setAddressEveningQuality(null);
          // Reset parent relations
          setStudentCurrentParents([]);
          // Reset loaded item
          setLoadedItem(undefined);
        }}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  const linkedRelations = relations.filter((relation) => relation.student === itemId);
  const linkedParentNames = linkedRelations.map((relation) => userMap[relation.parent]?.name).filter(Boolean);
  const deleteWarning =
    linkedRelations.length === 0
      ? undefined
      : linkedParentNames.length > 0
        ? t("student.deleteWithParentsWarning", { parents: linkedParentNames.join(", ") })
        : t("student.deleteWithParentsWarningUnnamed");

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemType}
        itemId={itemId}
        message={deleteWarning}
        onDeleted={() => {
          linkedRelations.forEach((relation) => DeleteRelation(relation.id));
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {/* <Modal
        show={showHelp}
        header="Yardım"
        footerClass="place-item__modal-actions"
        footer={
          <React.Fragment>
            <Button danger onClick={() => setShowHelp(false)}>
              Tamam
            </Button>
          </React.Fragment>
        }
      >
        <p>
          <ul>
            <li>
              <b>Açıklama</b> maddesi aynı isimli öğrencilerinizi ayırt etmek için kullanılır. Bu adıma "Öğrenci Ad
              Soyad - Veli Ad Soyad" bilgilerini yazmanızı öneririz.
            </li>
            <br />
            <li>
              <b>Öğrenci adres bilgileri</b> kısmında öğrencinin biniş ve iniş adreslerini giriniz. Adres aynı olsa dahi
              bu bilginin hem biniş, hem iniş adımlarına girilmesi gerekmektedir.
            </li>
            <br />
            <li>
              <b>Biniş/İniş adresinden konum getir</b> tuşlarına basmanız ve haritalar üzerinde öğrencinin adresinin
              doğru şekilde konumlandığını kontrol etmeniz gerekmektedir. Eğer harita üzerinde adresiniz doğru
              konumlandırılmamışsa öğrenci servis uygulamasını <b>kullanamayacaktır</b>.
            </li>
            <br />
            <li>
              <b>Öğrenci eğer artık okulunuz bünyesinde servis kullanmayacaksa </b> Durum bilgisini Pasif olarak
              işaretleyiniz. Pasif durumdaki öğrenciler yukarıdaki <b>Analiz</b> sekmesindeki adımlarda dikkate
              alınmazlar. Bir öğrenci okulunuzdan ayrıldığında öğrenciyi silmek yerine kullanım durumunu pasif'e
              çekmeniz gerekmektedir. Böylece öğrenciye ait geçmiş muhasebe bilgilerine ulaşabilirsiniz ve öğrencinin
              okulunuza geri dönmesi durumunda tekrar kayıt oluşturmanıza gerek kalmaz.
            </li>
            <br />
            <li>
              <b>Öğrenci silme işlemini </b> yalnızca çok bariz bir şekilde hatalı giriş yaptığınızda son seçenek olarak
              kullanınız. Daha önce servis kullanmış, sözleşme ve muhasebe kayıtları oluşturulmuş öğrencileri silmek
              yerine pasif duruma çekmeniz gerekmektedir.
            </li>
          </ul>
        </p>
      </Modal> */}

      {isItemLoaded && (
        <form className="form-container" onSubmit={itemUpdateSubmitHandler}>
          <GenericCard
            // title="Öğrenci Bilgileri"

            dismissible={false}
            onDismiss={() => console.log("Kart kapatıldı")}
            style={{ paddingBottom: "0px", marginBottom: "10px" }}
          >
            <div className="horizontal-flex">
              <CustomSelect
                options={studentSchoolOptions}
                id="school"
                onInput={inputHandler}
                label={t("school") + " *"}
                initialValue={
                  studentSchoolOptions.find((option) => option.value.toString() === loadedItem?.school.toString())
                    ? studentSchoolOptions.find((option) => option.value.toString() === loadedItem?.school.toString())
                    : studentSchoolOptions.length === 1
                      ? studentSchoolOptions[0]
                      : null
                }
                validators={[VALIDATOR_REQUIRE()]}
                initialValid={isNewItem ? false : true}
              ></CustomSelect>

              <CustomSelect
                options={isActiveOptions}
                id="isActive"
                onInput={inputHandler}
                label={t("studentStatus")}
                initialValue={
                  isActiveOptions.find((option) => option.value.toString() === loadedItem?.isActive.toString())
                    ? isActiveOptions.find((option) => option.value.toString() === loadedItem?.isActive.toString())
                    : isActiveOptions[1]
                }
                validators={[VALIDATOR_REQUIRE()]}
                initialValid={true}
                description={t("studentStatusDescription")}
              ></CustomSelect>
            </div>
            <div className="horizontal-flex">
              <Input
                id="name"
                element="input"
                type="text"
                label={t("studentName") + " *"}
                validators={[VALIDATOR_REQUIRE()]}
                errorText={t("requireField", { item: t("name") })}
                initialValue={loadedItem?.name}
                initialValid={isNewItem ? false : true}
                onInput={inputHandler}
              />

              <Input
                id="level"
                element="input"
                type="text"
                label={t("studentGrade")}
                initialValue={loadedItem?.level}
                validators={[VALIDATOR_NONE()]}
                initialValid={true}
                onInput={inputHandler}
                description={t("studentLevelDescription")}
              />
            </div>

            <Input
              id="identifier"
              element="input"
              type="text"
              label={t("identifier")}
              validators={[VALIDATOR_NONE()]}
              initialValue={loadedItem?.identifier || ""}
              initialValid={true}
              onInput={inputHandler}
              errorText={t("studentDescriptionError")}
              description={t("studentIdentifierDescription")}
            />
          </GenericCard>

          <GenericCard
            title={t("parentInformation")}
            dismissible={false}
            onDismiss={() => console.log("Kart kapatıldı")}
            style={{ marginBottom: "10px" }}
          >
            {/* <h4 style={{ marginBottom: "10px" }}>{t("parentsOftheStudent")}</h4> */}
            {studentCurrentParents.length > 0 && (
              <div className="vertical-flex" style={{ rowGap: "10px", marginBottom: "20px", marginTop: "10px" }}>
                {studentCurrentParents.map((parent) => (
                  <ParentRelationRow
                    key={parent.id}
                    parentId={parent.id}
                    studentId={itemId}
                    onDelete={async (parentId) => {
                      // Öğrenciden bu veliyi kaldır
                      setStudentCurrentParents((prevParents) => prevParents.filter((p) => p.id !== parentId));
                    }}
                    onChange={(relation) => {
                      setStudentCurrentParents((prevParents) =>
                        prevParents.map((p) => (p.id === relation.parent ? { ...p, ...relation } : p)),
                      );
                    }}
                    onInit={(relation) => {
                      setStudentCurrentParents((prevParents) =>
                        prevParents.map((p) => (p.id === relation.parent ? { ...p, ...relation } : p)),
                      );
                    }}
                    //onInit={(relation) => {}}
                    isNewItem={isNewItem}
                    inModal={false}
                  />
                ))}
              </div>
            )}
            <AddParentButton
              onAddPresentParent={(parentId) => {
                if (studentCurrentParents.some((p) => p?.id === parentId)) {
                  return;
                }
                setStudentCurrentParents((prevParents) => [...prevParents, userMap[parentId]]);
              }}
              onAddNewParent={(parent) => {
                if (studentCurrentParents.some((p) => p?.id === parent.id)) {
                  return;
                }
                setStudentCurrentParents((prevParents) => [...prevParents, parent]);
              }}
            />
          </GenericCard>

          <GenericCard
            dismissible={false}
            onDismiss={() => console.log("Kart kapatıldı")}
            style={{ marginBottom: "20px", paddingBottom: "0px" }}
          >
            <MapSinglePoint
              id="studentMorningItemMap"
              title={t("pickUpAddress")}
              label={t("pickUpAddress")}
              initialAddress={studentAddressMorning || ""}
              initialAddressInSync={addressMorningInSync}
              initialLocation={markerLocationMorning}
              initialAddressQuality={addressMorningQuality}
              onInput={inputHandler}
              onMapDataChange={mapDataChangeHandlerMorning}
              isRequired={true}
            ></MapSinglePoint>
            {/* </div> */}
            {/* <div style={{ marginBottom: "20px" }}></div> */}
            {/* <div style={{ flex: 1 }}> */}
            <MapSinglePoint
              id="studentEveningItemMap"
              title={t("dropOffAddress")}
              label={t("dropOffAddress")}
              initialAddress={studentAddressEvening || ""}
              initialAddressInSync={addressEveningInSync}
              initialLocation={markerLocationEvening}
              initialAddressQuality={addressEveningQuality}
              onInput={inputHandler}
              onMapDataChange={mapDataChangeHandlerEvening}
              isRequired={true}
            ></MapSinglePoint>
          </GenericCard>
          {/* </div> */}
          {/* </div> */}
          {/* <Input
            id="phonenumber"
            element="input"
            type="text"
            label="Telefon"
            //errorText="Telefon giriniz."
            validators={[VALIDATOR_NONE()]}
            initialValue={loadedItem.phonenumber}
            initialValid={true}
            onInput={inputHandler}
          />
          <Input
            id="email"
            element="input"
            type="text"
            label="Email"
            errorText="Email giriniz."
            onInput={inputHandler}
            initialValue={loadedItem.email}
            initialValid={true}
            validators={[VALIDATOR_NONE()]}
          /> */}

          <FormFooter
            isNewItem={isNewItem}
            onUpdate={itemUpdateSubmitHandler}
            onDelete={() => setShowConfirmModal(true)}
            isUpdating={isLoading}
            style={{ paddingLeft: "20px", paddingRight: "20px" }}
            disabled={
              !formState.inputs.school.value ||
              !formState.isValid ||
              !addressMorningInSync ||
              !addressEveningInSync ||
              isLoading
            }
          />
        </form>
      )}
    </React.Fragment>
  );
};

export default StudentItem;
